import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Panel,
  PanelType,
  Stack,
  Text,
  TextField,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  IconButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Dialog,
  DialogType,
  DialogFooter,
  mergeStyleSets,
} from "@fluentui/react";
import { X } from "@phosphor-icons/react";

import {
  apiGetColumns,
  apiGetSchemas,
  apiGetViews,
  apiUpsertDataset,
  slugify,
  type CatalogueDataset,
  type IntrospectColumn,
} from "./datasetAdminApi";
import type { Agent } from "../types/agent.types";
import type { CatalogueTableMapping } from "../types/dataset.types";

const styles = mergeStyleSets({
  // Mirrors EditAccessPanel section scaffolding so the two panels feel
  // like the same component family.
  section: { paddingTop: 8 },
  sectionHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
    color: "#232323",
    paddingBottom: 6,
    borderBottom: "1px solid #EDEDED",
    marginBottom: 12,
  },
  sectionIndex: {
    fontSize: 11,
    fontWeight: 600,
    color: "#FFFFFF",
    background: "#2e873d",
    width: 18,
    height: 18,
    borderRadius: 9,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sectionNote: {
    fontSize: 12,
    color: "#5C5C5C",
    padding: "10px 12px",
    background: "#F8F9FA",
    border: "1px solid #EDEDED",
    borderRadius: 4,
  },
  panelFooter: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 16px",
    borderTop: "1px solid #E1E1E1",
    background: "#FFFFFF",
  },
  dirtyIndicator: { fontSize: 12, color: "#2e873d", fontWeight: 500 },
  dirtyDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: 4,
    background: "#2e873d",
    marginRight: 6,
    verticalAlign: "middle",
  },
  footerSpacer: { flex: 1 },

  // Two-column row used inside Section 2 (Configuration) to pair short
  // related fields (Icon + Color, Classification + Status).
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },

  // Section 3 — Assigned tables. Each pill is one (schema, view) mapping;
  // the picker below adds new mappings.
  tablePillRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 6,
    marginBottom: 10,
  },
  tablePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#F4FAF5",
    border: "1px solid #B7DDB7",
    color: "#1a5c26",
    borderRadius: 14,
    padding: "4px 10px 4px 12px",
    fontSize: 12,
  },
  tablePillName: { fontWeight: 600 },
  tablePillSub: { fontSize: 10, color: "#5C7A60" },
  tablePillCols: { fontSize: 10, color: "#2e873d" },
  picker: {
    padding: 12,
    background: "#FAFAFA",
    border: "1px solid #EDEDED",
    borderRadius: 6,
  },
  pickerTitle: {
    fontWeight: 600,
    fontSize: 12,
    color: "#484848",
    display: "block",
    marginBottom: 8,
  },
  columnList: {
    maxHeight: 160,
    overflowY: "auto" as const,
    border: "1px solid #EDEDED",
    borderRadius: 4,
    padding: 4,
    background: "#FFFFFF",
  },
  columnRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 4px",
    fontSize: 11,
    cursor: "pointer",
  },
});

const CLASSIFICATION_OPTIONS: IDropdownOption[] = [
  { key: "OFFICIAL", text: "OFFICIAL" },
  { key: "OFFICIAL: Sensitive", text: "OFFICIAL: Sensitive" },
  { key: "PROTECTED", text: "PROTECTED" },
];

const STATUS_OPTIONS: IDropdownOption[] = [
  { key: "active", text: "Active" },
  { key: "coming_soon", text: "Coming Soon" },
];

type Mode = "add" | "edit";

type Props = {
  open: boolean;
  mode: Mode;
  dataset: CatalogueDataset | null;
  agents: Agent[];
  onClose: () => void;
  onSaved: () => void;
};

const EditDatasetPanel: React.FC<Props> = ({ open, mode, dataset, agents, onClose, onSaved }) => {
  const [id, setId] = useState("");
  const [department, setDepartment] = useState("");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [icon, setIcon] = useState("Database");
  const [color, setColor] = useState("#6366F1");
  const [classification, setClassification] = useState("OFFICIAL");
  const [agentId, setAgentId] = useState("");
  const [status, setStatus] = useState<"active" | "coming_soon">("coming_soon");
  const [tables, setTables] = useState<CatalogueTableMapping[]>([]);

  // Cascading add-table picker state.
  const [availableSchemas, setAvailableSchemas] = useState<string[]>([]);
  const [selectedSchema, setSelectedSchema] = useState("");
  const [availableViews, setAvailableViews] = useState<string[]>([]);
  const [selectedView, setSelectedView] = useState("");
  const [availableColumns, setAvailableColumns] = useState<IntrospectColumn[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [newTableDisplayName, setNewTableDisplayName] = useState("");
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [loadingViews, setLoadingViews] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  // Snapshot of initial form state — used by dirtyCount + the dismiss guard.
  type Snapshot = {
    id: string;
    department: string;
    description: string;
    owner: string;
    ownerEmail: string;
    icon: string;
    color: string;
    classification: string;
    agentId: string;
    status: "active" | "coming_soon";
    tables: string; // JSON-stringified for stable equality
  };
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);

  // Hydrate from props every time the panel opens.
  useEffect(() => {
    if (!open) return;
    const initId = dataset?.id ?? "";
    const initDept = dataset?.department ?? "";
    const initDesc = dataset?.description ?? "";
    const initOwner = dataset?.owner ?? "";
    const initOwnerEmail = dataset?.ownerEmail ?? "";
    const initIcon = dataset?.icon ?? "Database";
    const initColor = dataset?.color ?? "#6366F1";
    const initClass = dataset?.classification ?? "OFFICIAL";
    const initAgent = dataset?.agentId ?? "";
    const initStatus = dataset?.status ?? "coming_soon";
    const initTables = dataset?.tables ?? [];

    setId(initId);
    setDepartment(initDept);
    setDescription(initDesc);
    setOwner(initOwner);
    setOwnerEmail(initOwnerEmail);
    setIcon(initIcon);
    setColor(initColor);
    setClassification(initClass);
    setAgentId(initAgent);
    setStatus(initStatus);
    setTables(initTables);
    setSelectedSchema("");
    setSelectedView("");
    setAvailableViews([]);
    setAvailableColumns([]);
    setSelectedColumns([]);
    setNewTableDisplayName("");
    setError(null);
    setDiscardDialogOpen(false);
    setSnapshot({
      id: initId,
      department: initDept,
      description: initDesc,
      owner: initOwner,
      ownerEmail: initOwnerEmail,
      icon: initIcon,
      color: initColor,
      classification: initClass,
      agentId: initAgent,
      status: initStatus,
      tables: JSON.stringify(initTables),
    });
  }, [open, dataset]);

  // Classification → introspect source. OFFICIAL goes to Postgres (`pg`);
  // OFFICIAL: Sensitive / PROTECTED go to Microsoft Fabric warehouse.
  const introspectSource = classification === "OFFICIAL" ? "pg" : "fabric";
  const defaultSchema = classification === "OFFICIAL" ? "public" : "reporting";

  // Load schemas whenever the panel opens or classification toggles.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingSchemas(true);
    setAvailableSchemas([]);
    setSelectedSchema("");
    setAvailableViews([]);
    setSelectedView("");
    setAvailableColumns([]);
    setSelectedColumns([]);
    (async () => {
      try {
        const data = await apiGetSchemas(introspectSource);
        if (!cancelled) {
          setAvailableSchemas(Array.isArray(data) ? data : []);
          if (data.includes(defaultSchema)) setSelectedSchema(defaultSchema);
          else if (data.length > 0) setSelectedSchema(data[0]);
        }
      } catch (e) {
        console.error("Failed to load schemas:", e);
        if (!cancelled) setAvailableSchemas([]);
      } finally {
        if (!cancelled) setLoadingSchemas(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, introspectSource, defaultSchema]);

  // Load views when the chosen schema changes.
  useEffect(() => {
    if (!open || !selectedSchema) {
      setAvailableViews([]);
      return;
    }
    let cancelled = false;
    setLoadingViews(true);
    setAvailableViews([]);
    setSelectedView("");
    setAvailableColumns([]);
    setSelectedColumns([]);
    (async () => {
      try {
        const data = await apiGetViews(introspectSource, selectedSchema);
        if (!cancelled) setAvailableViews(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to load views:", e);
        if (!cancelled) setAvailableViews([]);
      } finally {
        if (!cancelled) setLoadingViews(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, introspectSource, selectedSchema]);

  // Load columns + auto-derive a friendly display name when the view changes.
  useEffect(() => {
    if (!open || !selectedSchema || !selectedView) {
      setAvailableColumns([]);
      return;
    }
    let cancelled = false;
    setLoadingColumns(true);
    setAvailableColumns([]);
    setSelectedColumns([]);
    setNewTableDisplayName(
      selectedView.replace(/^vw_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    );
    (async () => {
      try {
        const data = await apiGetColumns(introspectSource, selectedSchema, selectedView);
        if (!cancelled) {
          setAvailableColumns(Array.isArray(data) ? data : []);
          setSelectedColumns([]);
        }
      } catch (e) {
        console.error("Failed to load columns:", e);
        if (!cancelled) setAvailableColumns([]);
      } finally {
        if (!cancelled) setLoadingColumns(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, introspectSource, selectedSchema, selectedView]);

  const handleAddTableMapping = useCallback(() => {
    if (!selectedSchema || !selectedView) return;
    if (tables.some((t) => t.schema === selectedSchema && t.view === selectedView)) return;
    const newMapping: CatalogueTableMapping = {
      schema: selectedSchema,
      view: selectedView,
      displayName: newTableDisplayName || selectedView,
      columns: selectedColumns,
    };
    setTables([...tables, newMapping]);
    setSelectedView("");
    setAvailableColumns([]);
    setSelectedColumns([]);
    setNewTableDisplayName("");
  }, [selectedSchema, selectedView, newTableDisplayName, selectedColumns, tables]);

  const dsAgentOptions = useMemo<IDropdownOption[]>(
    () => [{ key: "", text: "None" }, ...agents.map((a) => ({ key: a.id, text: a.name }))],
    [agents]
  );

  const addViewOptions = useMemo<IDropdownOption[]>(
    () =>
      availableViews
        .filter((v) => !tables.some((t) => t.schema === selectedSchema && t.view === v))
        .map((v) => ({ key: v, text: v })),
    [availableViews, tables, selectedSchema]
  );

  // Field-level diff. JSON.stringify on tables is fine for the small
  // arrays involved here — this isn't on the hot path.
  const dirtyCount = useMemo(() => {
    if (!snapshot) return 0;
    let n = 0;
    if (id !== snapshot.id) n++;
    if (department !== snapshot.department) n++;
    if (description !== snapshot.description) n++;
    if (owner !== snapshot.owner) n++;
    if (ownerEmail !== snapshot.ownerEmail) n++;
    if (icon !== snapshot.icon) n++;
    if (color !== snapshot.color) n++;
    if (classification !== snapshot.classification) n++;
    if (agentId !== snapshot.agentId) n++;
    if (status !== snapshot.status) n++;
    if (JSON.stringify(tables) !== snapshot.tables) n++;
    return n;
  }, [snapshot, id, department, description, owner, ownerEmail, icon, color, classification, agentId, status, tables]);

  const isDirty = dirtyCount > 0;

  function requestDismiss() {
    if (isDirty) setDiscardDialogOpen(true);
    else onClose();
  }
  function confirmDiscard() {
    setDiscardDialogOpen(false);
    onClose();
  }

  const canSave = useMemo(() => {
    if (saving) return false;
    if (!department.trim()) return false;
    if (!id.trim()) return false;
    if (mode === "edit" && !isDirty) return false;
    return true;
  }, [saving, department, id, mode, isDirty]);

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const payload: CatalogueDataset = {
        id,
        department,
        icon,
        color,
        classification,
        owner,
        ownerEmail,
        agentId: agentId || null,
        description,
        tables,
        status,
      };
      await apiUpsertDataset(payload);
      onSaved();
      onClose();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to save dataset");
    } finally {
      setSaving(false);
    }
  }

  const headerText = mode === "add"
    ? "Add dataset"
    : `Edit dataset — ${dataset?.department || dataset?.id || ""}`;

  const onRenderFooter = () => (
    <div className={styles.panelFooter}>
      {isDirty ? (
        <span className={styles.dirtyIndicator}>
          <span className={styles.dirtyDot} />
          {dirtyCount} unsaved {dirtyCount === 1 ? "change" : "changes"}
        </span>
      ) : null}
      <span className={styles.footerSpacer} />
      <DefaultButton text="Cancel" onClick={requestDismiss} disabled={saving} />
      <PrimaryButton
        text={saving ? "Saving…" : mode === "add" ? "Create dataset" : "Save changes"}
        onClick={handleSave}
        disabled={!canSave}
      />
    </div>
  );

  return (
    <>
      <Panel
        isOpen={open}
        onDismiss={requestDismiss}
        type={PanelType.medium}
        headerText={headerText}
        isFooterAtBottom
        onRenderFooterContent={onRenderFooter}
        isBlocking={false}
        closeButtonAriaLabel="Close"
      >
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { paddingTop: 8 } }}>
          {error && (
            <MessageBar messageBarType={MessageBarType.error} isMultiline>
              {error}
            </MessageBar>
          )}

          {/* Section 1 — Identity. The primary "what is this dataset" fields.
              Dataset ID auto-derives from the department name in add mode and
              becomes read-only in edit mode (it's the API PK). */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIndex}>1</span>
              <span>Identity</span>
            </div>

            <Stack tokens={{ childrenGap: 12 }}>
              <TextField
                label="Department name"
                required
                value={department}
                onChange={(_, v) => {
                  setDepartment(v ?? "");
                  if (mode === "add") setId(slugify(v ?? ""));
                }}
              />
              <TextField
                label="Dataset ID"
                value={id}
                readOnly={mode === "edit"}
                onChange={(_, v) => { if (mode === "add") setId(v ?? ""); }}
                description={mode === "edit" ? "Read-only — the ID is the API primary key." : "Auto-derived from the department name. Override before saving if needed."}
                styles={{ root: { opacity: mode === "edit" ? 0.6 : 1 } }}
              />
              <TextField
                label="Description"
                multiline
                rows={3}
                value={description}
                onChange={(_, v) => setDescription(v ?? "")}
              />
              <TextField label="Owner" value={owner} onChange={(_, v) => setOwner(v ?? "")} />
              <TextField label="Owner email" value={ownerEmail} onChange={(_, v) => setOwnerEmail(v ?? "")} />
            </Stack>
          </div>

          {/* Section 2 — Configuration. Visual presentation + routing. */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIndex}>2</span>
              <span>Configuration</span>
            </div>

            <Stack tokens={{ childrenGap: 12 }}>
              <div className={styles.twoCol}>
                <TextField
                  label="Icon"
                  placeholder='e.g. "Money", "Database"'
                  value={icon}
                  onChange={(_, v) => setIcon(v ?? "")}
                />
                <TextField
                  label="Color"
                  placeholder='e.g. "#7C7CFF"'
                  value={color}
                  onChange={(_, v) => setColor(v ?? "")}
                />
              </div>
              <div className={styles.twoCol}>
                <Dropdown
                  label="Classification"
                  selectedKey={classification}
                  options={CLASSIFICATION_OPTIONS}
                  onChange={(_, opt) => setClassification(String(opt?.key ?? "OFFICIAL"))}
                />
                <Dropdown
                  label="Status"
                  selectedKey={status}
                  options={STATUS_OPTIONS}
                  onChange={(_, opt) => setStatus((opt?.key as "active" | "coming_soon") ?? "coming_soon")}
                />
              </div>
              <Dropdown
                label="Agent"
                selectedKey={agentId}
                options={dsAgentOptions}
                onChange={(_, opt) => setAgentId(String(opt?.key ?? ""))}
              />
              <div className={styles.sectionNote}>
                Classification routes the dataset: <strong>OFFICIAL</strong> reads from
                Postgres (<code>edp.public</code>); <strong>OFFICIAL: Sensitive</strong> /
                <strong> PROTECTED</strong> read from the Microsoft Fabric warehouse.
                Changing classification reloads the schema list below.
              </div>
            </Stack>
          </div>

          {/* Section 3 — Assigned tables. Pills for current mappings + a
              cascading picker (Schema → View → Display name + Columns) for
              adding new ones. */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIndex}>3</span>
              <span>Assigned tables</span>
              <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 400, color: "#6B6B6B" }}>
                {tables.length} {tables.length === 1 ? "table" : "tables"}
              </span>
            </div>

            <div className={styles.tablePillRow}>
              {tables.length === 0 ? (
                <Text styles={{ root: { fontSize: 12, color: "#747474" } }}>
                  No tables assigned.
                </Text>
              ) : (
                tables.map((mapping) => (
                  <span
                    key={`${mapping.schema}.${mapping.view}`}
                    className={styles.tablePill}
                    title={`${mapping.schema}.${mapping.view}${mapping.columns.length > 0 ? ` (${mapping.columns.length} cols)` : " (all cols)"}`}
                  >
                    <span className={styles.tablePillName}>{mapping.displayName || mapping.view}</span>
                    <span className={styles.tablePillSub}>{mapping.schema}.{mapping.view}</span>
                    {mapping.columns.length > 0 && (
                      <span className={styles.tablePillCols}>({mapping.columns.length} cols)</span>
                    )}
                    <IconButton
                      onRenderIcon={() => <X size={10} />}
                      title={`Remove ${mapping.view}`}
                      onClick={() => setTables(tables.filter((t) => t.view !== mapping.view || t.schema !== mapping.schema))}
                      styles={{
                        root: { height: 18, width: 18, color: "#5C7A60" },
                        icon: { fontSize: 10 },
                        rootHovered: { color: "#A4262C", background: "transparent" },
                      }}
                    />
                  </span>
                ))
              )}
            </div>

            <div className={styles.picker}>
              <Text className={styles.pickerTitle}>Add table</Text>
              <Stack tokens={{ childrenGap: 8 }}>
                <Dropdown
                  label="Schema"
                  selectedKey={selectedSchema}
                  options={availableSchemas.map((s) => ({ key: s, text: s }))}
                  onChange={(_, opt) => setSelectedSchema(String(opt?.key ?? ""))}
                  placeholder={loadingSchemas ? "Loading schemas…" : "Select schema"}
                  disabled={loadingSchemas || availableSchemas.length === 0}
                />
                <Dropdown
                  label="View / table"
                  selectedKey={selectedView}
                  options={addViewOptions}
                  onChange={(_, opt) => setSelectedView(String(opt?.key ?? ""))}
                  placeholder={loadingViews ? "Loading views…" : selectedSchema ? `Select view (${addViewOptions.length} available)` : "Select a schema first"}
                  disabled={loadingViews || !selectedSchema || addViewOptions.length === 0}
                />

                {selectedView && (
                  <>
                    <TextField
                      label="Display name"
                      value={newTableDisplayName}
                      onChange={(_, v) => setNewTableDisplayName(v ?? "")}
                    />

                    <div>
                      <Text styles={{ root: { fontWeight: 600, fontSize: 12, color: "#323130", display: "block", marginBottom: 4 } }}>
                        Columns {loadingColumns ? "(loading…)" : `(${availableColumns.length} available)`}
                      </Text>
                      {loadingColumns && <Spinner size={SpinnerSize.xSmall} />}
                      {!loadingColumns && availableColumns.length > 0 && (
                        <>
                          <div style={{ marginBottom: 4 }}>
                            <label style={{ fontSize: 11, color: "#484848", cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={selectedColumns.length === 0}
                                onChange={() => setSelectedColumns([])}
                                style={{ marginRight: 4 }}
                              />
                              All columns (no restriction)
                            </label>
                          </div>
                          <div className={styles.columnList}>
                            {availableColumns.map((col) => (
                              <label key={col.column_name} className={styles.columnRow}>
                                <input
                                  type="checkbox"
                                  checked={selectedColumns.length === 0 || selectedColumns.includes(col.column_name)}
                                  onChange={() => {
                                    if (selectedColumns.length === 0) {
                                      setSelectedColumns(availableColumns.map((c) => c.column_name).filter((cn) => cn !== col.column_name));
                                    } else if (selectedColumns.includes(col.column_name)) {
                                      const next = selectedColumns.filter((cn) => cn !== col.column_name);
                                      setSelectedColumns(next.length === 0 ? [] : next);
                                    } else {
                                      const next = [...selectedColumns, col.column_name];
                                      setSelectedColumns(next.length === availableColumns.length ? [] : next);
                                    }
                                  }}
                                />
                                <span>{col.column_name}</span>
                                <span style={{ color: "#747474", marginLeft: "auto" }}>{col.data_type}</span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    <PrimaryButton
                      text="Add table"
                      onClick={handleAddTableMapping}
                      disabled={!selectedSchema || !selectedView}
                      styles={{ root: { marginTop: 4, alignSelf: "flex-start" } }}
                    />
                  </>
                )}
              </Stack>
            </div>
          </div>
        </Stack>
      </Panel>

      <Dialog
        hidden={!discardDialogOpen}
        onDismiss={() => setDiscardDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: `Discard ${dirtyCount} unsaved ${dirtyCount === 1 ? "change" : "changes"}?`,
          subText:
            "Your changes won't be saved. This can't be undone — you'll need to re-enter them if you change your mind.",
        }}
      >
        <DialogFooter>
          <PrimaryButton
            text="Discard changes"
            onClick={confirmDiscard}
            styles={{ root: { background: "#A4262C", borderColor: "#A4262C" } }}
          />
          <DefaultButton text="Keep editing" onClick={() => setDiscardDialogOpen(false)} />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default EditDatasetPanel;
